import { types } from 'mobx-state-tree';
import { api } from 'src/api';
import { isHttpClientCancel } from 'src/core/services/api/httpClient';
import { createAsync } from 'src/utils/asyncModel';

/** Statuses reported by the bill automation service. */
export const BILL_AUTOMATION_STATUS = {
  processing: 'Processing',
  extracted: 'Extracted',
  validated: 'Validated',
  failed: 'Failed'
};

/** A bill stops being polled after this many consecutive failed status requests. */
const MAX_FAILED_POLLS = 3;

/**
 * Validation arrives through a Kafka event, so a bill can sit in Extracted indefinitely if that
 * consumer is down. Give up after roughly ten minutes instead of polling for the whole session.
 */
const MAX_POLLS_PER_BILL = 120;
const UploadedBill = types.model('UploadedBill', {
  billId: types.identifier,
  propertyId: types.optional(types.string, ''),
  propertyAddress: types.optional(types.string, ''),
  fileName: types.optional(types.string, ''),
  /** Object URL of the uploaded file, so details can preview it without a file service round trip. */
  fileUrl: types.optional(types.string, ''),
  fileSizeMB: types.optional(types.number, 0),
  /** Utility types sent with the upload, so the row has utilities before extraction finishes. */
  requestedUtilityTypes: types.optional(types.array(types.string), []),
  status: types.optional(types.string, BILL_AUTOMATION_STATUS.processing),
  uploadedAt: types.optional(types.string, ''),
  /** When extraction reached a final status, so the details view can report how long it took. */
  extractedAt: types.optional(types.string, ''),
  data: types.optional(types.frozen(), null),
  /** User edits of the extracted payload. Kept next to `data` so the original stays auditable. */
  editedData: types.optional(types.frozen(), null),
  /** Per property/utility allocation inputs (service dates, bill amount, CAD/GWD deductions). */
  allocationSettings: types.optional(types.frozen(), {}),
  /** Allocation keys approved in this session. Nothing is sent anywhere until the API exists. */
  approvedAllocations: types.optional(types.frozen(), []),
  /** When the bill was pushed to Appfolio in this session. No request is made until the API exists. */
  appfolioPushedAt: types.optional(types.string, ''),
  errors: types.optional(types.frozen(), []),
  warnings: types.optional(types.frozen(), []),
  failedPollCount: types.optional(types.number, 0),
  pollCount: types.optional(types.number, 0)
}).views(store => ({
  get isFinalStatus() {
    return store.status === BILL_AUTOMATION_STATUS.validated || store.status === BILL_AUTOMATION_STATUS.failed;
  },
  /** Validated and failed bills are final, so they drop out of the polling sweep. */
  get isPollable() {
    const isPending = store.status === BILL_AUTOMATION_STATUS.processing || store.status === BILL_AUTOMATION_STATUS.extracted;
    return isPending && store.failedPollCount < MAX_FAILED_POLLS && store.pollCount < MAX_POLLS_PER_BILL;
  },
  /** What the details view renders: user edits when present, otherwise the raw extraction. */
  get effectiveData() {
    var _store$editedData;
    return (_store$editedData = store.editedData) !== null && _store$editedData !== void 0 ? _store$editedData : store.data;
  },
  get isPushedToAppfolio() {
    return Boolean(store.appfolioPushedAt);
  },
  /** Seconds spent between upload and the final status, for the "Thought for X s" line. */
  get extractionSeconds() {
    if (!store.extractedAt || !store.uploadedAt) return null;
    const seconds = (new Date(store.extractedAt).getTime() - new Date(store.uploadedAt).getTime()) / 1000;
    return Number.isFinite(seconds) && seconds >= 0 ? Math.round(seconds) : null;
  }
})).actions(store => ({
  applyStatusResponse(response) {
    var _response$data, _response$errors, _response$warnings;
    store.status = (response === null || response === void 0 ? void 0 : response.status) || store.status;
    store.data = (_response$data = response === null || response === void 0 ? void 0 : response.data) !== null && _response$data !== void 0 ? _response$data : store.data;
    store.errors = (_response$errors = response === null || response === void 0 ? void 0 : response.errors) !== null && _response$errors !== void 0 ? _response$errors : [];
    store.warnings = (_response$warnings = response === null || response === void 0 ? void 0 : response.warnings) !== null && _response$warnings !== void 0 ? _response$warnings : [];
    store.failedPollCount = 0;
    if (store.isFinalStatus && !store.extractedAt) store.extractedAt = new Date().toISOString();
  },
  setEditedData(data) {
    store.editedData = data;
  },
  setAllocationSettings(settings) {
    store.allocationSettings = settings;
  },
  setApprovedAllocations(keys) {
    store.approvedAllocations = keys;
  },
  pushToAppfolio() {
    store.appfolioPushedAt = new Date().toISOString();
  },
  registerPollAttempt() {
    store.pollCount += 1;
  },
  registerPollFailure() {
    store.failedPollCount += 1;
  }
}));
const pollUploadedBillsAsync = createAsync(() => {
  return async function pollUploadedBills(options) {
    const billIds = this.pollableBills.map(bill => bill.billId);

    // Sequential so a single cancel token covers the sweep and requests never overlap.
    for (const billId of billIds) {
      try {
        this.registerPollAttempt(billId);
        const resp = await api.BillAutomationBills.getBill(billId, options);
        if (resp !== null && resp !== void 0 && resp.isSuccessful && resp !== null && resp !== void 0 && resp.response) this.applyBillStatus(billId, resp.response);else this.registerPollFailure(billId);
      } catch (error) {
        if (isHttpClientCancel(error)) throw error;
        // A flaky status request should not raise a toast every few seconds.
        this.registerPollFailure(billId);
      }
    }
  };
});

/**
 * Bills uploaded in the current session. Runtime only: reloading the app clears them,
 * so nothing here is treated as a persisted record.
 */
const UploadedBillsStore = types.model('UploadedBillsStore', {
  bills: types.optional(types.array(UploadedBill), []),
  pollUploadedBillsAsync
}).actions(store => ({
  addUploadedBill(bill) {
    var _bill$propertyId, _bill$propertyAddress, _bill$fileName, _bill$fileUrl, _bill$fileSizeMB, _bill$requestedUtilit, _bill$data, _bill$errors, _bill$warnings;
    const existing = store.bills.find(i => i.billId === bill.billId);
    if (existing) {
      existing.applyStatusResponse(bill);
      return;
    }
    store.bills.unshift({
      billId: bill.billId,
      propertyId: (_bill$propertyId = bill.propertyId) !== null && _bill$propertyId !== void 0 ? _bill$propertyId : '',
      propertyAddress: (_bill$propertyAddress = bill.propertyAddress) !== null && _bill$propertyAddress !== void 0 ? _bill$propertyAddress : '',
      fileName: (_bill$fileName = bill.fileName) !== null && _bill$fileName !== void 0 ? _bill$fileName : '',
      fileUrl: (_bill$fileUrl = bill.fileUrl) !== null && _bill$fileUrl !== void 0 ? _bill$fileUrl : '',
      fileSizeMB: (_bill$fileSizeMB = bill.fileSizeMB) !== null && _bill$fileSizeMB !== void 0 ? _bill$fileSizeMB : 0,
      requestedUtilityTypes: (_bill$requestedUtilit = bill.requestedUtilityTypes) !== null && _bill$requestedUtilit !== void 0 ? _bill$requestedUtilit : [],
      status: bill.status || BILL_AUTOMATION_STATUS.processing,
      uploadedAt: bill.uploadedAt || bill.createdAt || new Date().toISOString(),
      data: (_bill$data = bill.data) !== null && _bill$data !== void 0 ? _bill$data : null,
      errors: (_bill$errors = bill.errors) !== null && _bill$errors !== void 0 ? _bill$errors : [],
      warnings: (_bill$warnings = bill.warnings) !== null && _bill$warnings !== void 0 ? _bill$warnings : []
    });
  },
  removeUploadedBill(billId) {
    const bill = store.bills.find(i => i.billId === billId);
    if (!bill) return;
    const fileUrl = bill.fileUrl;
    store.bills.remove(bill);
    // One upload can cover several properties, so the object URL is shared by all of its rows.
    if (fileUrl && !store.bills.some(i => i.fileUrl === fileUrl)) URL.revokeObjectURL(fileUrl);
  },
  applyBillStatus(billId, response) {
    var _store$bills$find;
    (_store$bills$find = store.bills.find(i => i.billId === billId)) === null || _store$bills$find === void 0 || _store$bills$find.applyStatusResponse(response);
  },
  setEditedData(billId, data) {
    var _store$bills$find2;
    (_store$bills$find2 = store.bills.find(i => i.billId === billId)) === null || _store$bills$find2 === void 0 || _store$bills$find2.setEditedData(data);
  },
  setAllocationSettings(billId, settings) {
    var _store$bills$find3;
    (_store$bills$find3 = store.bills.find(i => i.billId === billId)) === null || _store$bills$find3 === void 0 || _store$bills$find3.setAllocationSettings(settings);
  },
  setApprovedAllocations(billId, keys) {
    var _store$bills$find4;
    (_store$bills$find4 = store.bills.find(i => i.billId === billId)) === null || _store$bills$find4 === void 0 || _store$bills$find4.setApprovedAllocations(keys);
  },
  pushToAppfolio(billId) {
    var _store$bills$find5;
    (_store$bills$find5 = store.bills.find(i => i.billId === billId)) === null || _store$bills$find5 === void 0 || _store$bills$find5.pushToAppfolio();
  },
  registerPollAttempt(billId) {
    var _store$bills$find6;
    (_store$bills$find6 = store.bills.find(i => i.billId === billId)) === null || _store$bills$find6 === void 0 || _store$bills$find6.registerPollAttempt();
  },
  registerPollFailure(billId) {
    var _store$bills$find7;
    (_store$bills$find7 = store.bills.find(i => i.billId === billId)) === null || _store$bills$find7 === void 0 || _store$bills$find7.registerPollFailure();
  }
})).views(store => ({
  get pollableBills() {
    return store.bills.filter(bill => bill.isPollable);
  },
  get hasPollableBills() {
    return store.bills.some(bill => bill.isPollable);
  }
}));
export const uploadedBills = types.optional(UploadedBillsStore, {});